import { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import TagSelector, {
  SelectOption,
  IUserTagsState,
  IUserTagsProps,
} from 'corp/components/UI/TagSelector';
import { IUserJson } from '@scale/shared/types/user';
import { invokeCorpJob } from '@scale/frontend-lib/invokeCorpJob';
import _ from 'lodash';

// User Tag selector component
// Extends from base and overrides add / remove behavior to not only keep an
// up to date tag list but to also patch the user object as tags get added / removed
// from the list
class UserTags extends TagSelector<IUserTagsProps, IUserTagsState> {
  constructor(props: IUserTagsProps) {
    super(props);
    this.state = {
      tags: props.tags ? props.tags : [],
      pendingTagName: '',
      showAddTagDialog: false,
      userId: props.userId,
    };
  }

  componentDidUpdate(prevProps: IUserTagsProps) {
    if (!_.isEqualWith(prevProps.tags, this.props.tags)) {
      // Check if the tags prop has changed and update state
      this.setState({ tags: this.props.tags ? this.props.tags : [] });
    }
  }

  addTag = async (tag: SelectOption) => {
    try {
      tag.loading = true;

      // Search by name here since this might've been called from createTag.
      const startTags = this.state.tags;
      const startIdx = startTags.findIndex(t => t.name === tag.name);
      if (startIdx === -1) {
        startTags.push(tag);
      } else {
        startTags[startIdx] = tag;
      }
      this.setState({ tags: startTags });

      const completedJob = await invokeCorpJob('workerTag', {
        userIdsAndEmails: [this.props.userId],
        tagId: tag._id,
        isTagOrUntag: true,
      });
      if (completedJob.output) {
        const { succeeded } = completedJob.jsonOutput;
        if (!succeeded.some((u: Partial<IUserJson>) => u._id === this.props.userId)) {
          this.setState({ tags: this.state.tags.filter(t => t._id !== tag._id) });
          window.alert(`Failed to add tag ${tag.name} to user`);
        }
      } else {
        this.setState({ tags: this.state.tags.filter(t => t._id !== tag._id) });
        window.alert(`Failed to add tag ${tag.name} to user`);
      }
      tag.loading = false;

      // Tags might've changed between start and end.
      const endTags = this.state.tags;
      const endIdx = endTags.findIndex(t => t._id === tag._id);
      if (endIdx === -1) {
        window.alert(`Failed to find tag ${tag.name} in list`);
      } else {
        endTags[endIdx] = tag;
      }
      this.setState({ tags: endTags });

      if (this.props.onTagsChanged) {
        this.props.onTagsChanged(endTags);
      }
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
  };

  removeTag = async (tag: SelectOption) => {
    try {
      tag.loading = true;

      // Search by name here since this might've been called from createTag.
      const startTags = this.state.tags;
      const startIdx = startTags.findIndex(t => t._id === tag._id);
      if (startIdx === -1) {
        window.alert(`Failed to find tag ${tag.name} in list`);
      } else {
        startTags[startIdx] = tag;
      }
      this.setState({ tags: startTags });

      const completedJob = await invokeCorpJob('workerTag', {
        userIdsAndEmails: [this.props.userId],
        tagId: tag._id,
        isTagOrUntag: false,
      });

      if (completedJob.output) {
        const { succeeded } = completedJob.jsonOutput;
        if (!succeeded.some((u: Partial<IUserJson>) => u._id === this.props.userId)) {
          this.setState({ tags: [...this.state.tags, tag] });
          window.alert(`Failed to remove tag ${tag.name} from user`);
        }
      } else {
        this.setState({ tags: [...this.state.tags, tag] });
        window.alert(`Failed to remove tag ${tag.name} from user`);
      }

      const filteredTags = this.state.tags.filter(t => t._id !== tag._id);

      this.setState({ tags: filteredTags });

      if (this.props.onTagsChanged) {
        this.props.onTagsChanged(filteredTags);
      }
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
  };
}

export default UserTags;
